import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper";
import NewEmail from "../Inputs/NewEmail";
import NewPhone from "../Inputs/NewPhone";
import NewText from "../Inputs/NewText";
import NewTitle from "../Inputs/NewTitle";
import RadioGroupField from "../Inputs/RadioGroupField";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper";
import "../styles/form-container.css";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow";
import "./styles/generalinfo.css";

// Slide 1: General Information
const GeneralInfo = ({ setSlideStatus, formState, formStateLoaded }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide1,
    setSlide1,
    slide2,
    setSlide2,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    titles,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { projectId, clientId } = useContext(DashboardContext);

  // - On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide1);
  }, []);

  // Update the slide state
  const slide1Mutation = useSetSlide(
    clientId,
    projectId,
    1,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate invalid inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "1",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  useEffect(() => {
    console.log("slide1", slide1);
  }, [slide1]);

  return (
    <>
      <ResponsiveRow>
        <NewText
          idProp={"projectName"}
          label="Project Name"
          state={slide1}
          setState={setSlide1}
          val={slide1.projectName}
          req={slide1.projectName === ""}
          setIsLoading={setIsLoading}
          type="project"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("projectName", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("projectName", disabledInputs) === false
              ? getInputMessage("projectName", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("projectName", invalidInputsData, "1")}
        />
      </ResponsiveRow>
      <h2 className="question-section">Main Point of Contact Information</h2>
      <p className="important-text">
        {
          "Please provide the contact information for the individual to contact for this project"
        }
      </p>
      <ResponsiveRow>
        <NewText
          idProp={"pointOfContact"}
          label="First & Last Name"
          state={slide1}
          setState={setSlide1}
          val={slide1.pointOfContact}
          setIsLoading={setIsLoading}
          autoData={predictiveTextFetched ? names : []}
          req={!isValidName(slide1.pointOfContact, false)}
          type="name"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("pointOfContact", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("pointOfContact", disabledInputs) === false
              ? getInputMessage("pointOfContact", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("pointOfContact", invalidInputsData, "1")}
        />
        <NewEmail
          idProp={"pointOfContactEmail"}
          label="Email"
          state={slide1}
          setState={setSlide1}
          val={slide1.pointOfContactEmail}
          setIsLoading={setIsLoading}
          autoData={predictiveTextFetched ? emails : []}
          req={!isValidEmail(slide1.pointOfContactEmail, false)}
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck(
            "pointOfContactEmail",
            disabledInputs,
          )}
          disabledInputMessage={
            disabledInputCheck("pointOfContactEmail", disabledInputs) === false
              ? getInputMessage("pointOfContactEmail", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData(
            "pointOfContactEmail",
            invalidInputsData,
            "1",
          )}
        />
        <NewPhone
          idProp={"pointOfContactPhone"}
          label="Phone"
          state={slide1}
          setState={setSlide1}
          val={slide1.pointOfContactPhone}
          setIsLoading={setIsLoading}
          autoData={predictiveTextFetched ? phoneNumbers : []}
          req={!isValidPhoneNumber(slide1.pointOfContactPhone, false)}
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck(
            "pointOfContactPhone",
            disabledInputs,
          )}
          disabledInputMessage={
            disabledInputCheck("pointOfContactPhone", disabledInputs) === false
              ? getInputMessage("pointOfContactPhone", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData(
            "pointOfContactPhone",
            invalidInputsData,
            "1",
          )}
        />
        <NewTitle
          idProp={"pointOfContactTitle"}
          label="Title"
          state={slide1}
          setState={setSlide1}
          val={slide1.pointOfContactTitle}
          setIsLoading={setIsLoading}
          autoData={titles}
          req={false}
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck(
            "pointOfContactTitle",
            disabledInputs,
          )}
          disabledInputMessage={
            disabledInputCheck("pointOfContactTitle", disabledInputs) === false
              ? getInputMessage("pointOfContactTitle", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData(
            "pointOfContactTitle",
            invalidInputsData,
            "1",
          )}
        />
      </ResponsiveRow>
      <RadioGroupField
        idProp={"onNativeLand"}
        label="Is the project located on native or protected land?"
        state={slide1}
        setState={setSlide1}
        slide2={slide2}
        setSlide2={setSlide2}
        val={slide1.onNativeLand}
        setIsLoading={setIsLoading}
        debouncedSave={projectId ? slide1Mutation : undefined}
        disabledInput={disabledInputCheck("onNativeLand", disabledInputs)}
      />
      <RadioGroupField
        idProp={"underAcre"}
        label="Is the total disturbed area over 1 acre?"
        state={slide1}
        setState={setSlide1}
        val={slide1.underAcre}
        setIsLoading={setIsLoading}
        debouncedSave={projectId ? slide1Mutation : undefined}
        disabledInput={disabledInputCheck("underAcre", disabledInputs)}
      />
      <h2 className="question-section">Billing Information</h2>
      <p className="important-text">
        {
          "Please provide the name and email address of the person who will be receiving the invoice for this project, and the P.O. or job number associated with this project if applicable."
        }
      </p>
      <ResponsiveRow type={"center"}>
        <NewText
          idProp="accountingContact"
          label="Billing Contact Person"
          val={slide1.accountingContact}
          state={slide1}
          setState={setSlide1}
          setIsLoading={setIsLoading}
          req={!isValidName(slide1.accountingContact, false)}
          type="name"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck(
            "accountingContact",
            disabledInputs,
          )}
          disabledInputMessage={
            disabledInputCheck("accountingContact", disabledInputs) === false
              ? getInputMessage("accountingContact", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData(
            "accountingContact",
            invalidInputsData,
            "1",
          )}
        />
        <NewEmail
          idProp="accountingEmail"
          label="Invoice Email Address"
          state={slide1}
          setState={setSlide1}
          val={slide1.accountingEmail}
          setIsLoading={setIsLoading}
          autoData={predictiveTextFetched ? emails : []}
          req={
            !slide1.accountingEmail &&
            !isValidEmail(slide1.accountingEmail, false)
          }
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("accountingEmail", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("accountingEmail", disabledInputs) === false
              ? getInputMessage("accountingEmail", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData(
            "accountingEmail",
            invalidInputsData,
            "1",
          )}
        />
        <NewText
          idProp="jobNumber"
          label="P.O/Job Number"
          val={slide1.jobNumber}
          state={slide1}
          setState={setSlide1}
          setIsLoading={setIsLoading}
          req={!slide1.jobNumber}
          type="number"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("jobNumber", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("jobNumber", disabledInputs) === false
              ? getInputMessage("jobNumber", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("jobNumber", invalidInputsData, "1")}
        />
      </ResponsiveRow>
    </>
  );
};

export default GeneralInfo;
